import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sitewide promotional / pause banner. Auto-fetches /api/payment-settings
 * and only renders when the admin has enabled the banner OR payments are
 * paused. Detects the user's preferred language from <html lang> (set by
 * pages via `LanguageToggle`) and falls back to Hindi (the default).
 *
 * The banner is collapsible per-tab (sessionStorage) so the customer isn't
 * nagged after they've acknowledged it. It auto-rechecks every 60s so admin
 * toggles propagate to open tabs without requiring a refresh.
 */
const PromoBanner = () => {
  const [settings, setSettings] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      axios
        .get(`${API}/payment-settings`)
        .then(({ data }) => {
          if (cancelled) return;
          setSettings(data);
        })
        .catch(() => {});
    };
    load();
    const tick = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(tick);
    };
  }, []);

  // Admins are reviewing files — they don't want a sitewide promo/pause bar
  // covering content. Compute the flag now; the actual early-return happens
  // AFTER every hook below to keep the React Hooks rule-of-order intact.
  let isAdmin = false;
  try {
    isAdmin = typeof window !== "undefined" && window.sessionStorage.getItem("mn_admin") === "1";
  } catch {
    isAdmin = false;
  }

  // Read tab-scoped dismissal whenever the rendered message changes
  const lang = (typeof document !== "undefined" && document.documentElement.lang) || "hi";
  const paused = settings && settings.payments_enabled === false;
  const visible =
    settings &&
    !dismissed &&
    (paused || (settings.banner_enabled && (settings.banner_message_en || settings.banner_message_hi)));

  // Re-evaluate session dismissal whenever the message content changes so a
  // brand-new admin update re-shows the banner.
  const messageKey = settings
    ? `promo:${settings.banner_message_en}|${settings.banner_message_hi}|${paused ? settings.payments_disabled_message : ""}`
    : "";
  useEffect(() => {
    if (!messageKey) return;
    setDismissed(window.sessionStorage.getItem(messageKey) === "1");
  }, [messageKey]);

  if (isAdmin) return null;
  if (!visible) return null;

  const message = paused
    ? settings.payments_disabled_message ||
      (lang === "hi"
        ? "पेमेंट अस्थायी रूप से बंद है — कृपया कुछ देर बाद पुनः प्रयास करें।"
        : "Payments are temporarily paused. Please try again shortly.")
    : lang === "hi"
    ? settings.banner_message_hi || settings.banner_message_en
    : settings.banner_message_en || settings.banner_message_hi;

  const tone = paused ? "alert" : "promo";

  const handleDismiss = () => {
    setDismissed(true);
    if (messageKey) window.sessionStorage.setItem(messageKey, "1");
  };

  return (
    <div
      data-testid={paused ? "site-pause-banner" : "site-promo-banner"}
      className={`relative w-full text-[13px] sm:text-[14px] font-bold text-center px-9 py-2 ${
        tone === "alert"
          ? "bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white"
          : "bg-gradient-to-r from-gold-dark via-gold to-gold-dark text-navy"
      }`}
    >
      <span className="inline-flex items-center gap-1.5 leading-tight">
        {tone === "promo" && <Sparkles className="h-3.5 w-3.5" />}
        {message}
        {!paused && settings.discount_percent > 0 && (
          <span className="ml-1 px-1.5 py-0.5 rounded-md bg-navy text-gold text-[11px] font-extrabold tracking-wider">
            {settings.discount_percent}% OFF
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="site-banner-close"
        aria-label="Dismiss"
        className={`absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center rounded-full transition-colors ${
          tone === "alert" ? "hover:bg-white/15" : "hover:bg-navy/15"
        }`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default PromoBanner;
